/* ==========================================================================
   EZION — SIDE CART
   Opening, closing, and keeping the panel honest.

   Every mutation goes through /cart/change.js, and the panel then repaints
   from `?sections=cart-drawer`. Repainting the whole thing rather than
   patching the numbers in place is what keeps the four regions consistent:
   a removed line can move the free-shipping bar, drop a product back into
   the upsell rail and change the total in one go, and one round trip
   renders all of it from the same cart.
   ========================================================================== */

(function () {
  'use strict';

  var EZ = window.EZION || (window.EZION = {});
  EZ.routes = EZ.routes || {};
  EZ.strings = EZ.strings || {};

  /* The id the Section Rendering API answers to. Read from the DOM rather
     than hardcoded: as a static section it was literally 'cart-drawer', but
     inside a section group Shopify names it differently, and a stale constant
     silently re-renders the section with no stored settings — the drawer
     repaints itself without its upsell. */
  var SECTION = (function () {
    var host = document.querySelector('ez-cart-drawer[data-section-id]');
    return (host && host.getAttribute('data-section-id')) || 'cart-drawer';
  })();

  class EzCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.ez-sidecart__panel');

      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));

      // core.js toggles [data-open] on any [data-drawer]; watching the
      // attribute rather than the clicks means focus is handled the same way
      // whether the drawer was opened by the bag, an add-to-cart or Escape.
      this.watcher = new MutationObserver(this.onToggle.bind(this));
      this.watcher.observe(this, { attributes: true, attributeFilter: ['data-open'] });

      this.boundOpen = this.open.bind(this);
      this.boundRefresh = this.refresh.bind(this);
      this.boundPaint = this.paint.bind(this);
      EZ.openCartDrawer = this.boundOpen;
      EZ.refreshCartDrawer = this.boundRefresh;
      EZ.paintCartDrawer = this.boundPaint;

      // Makes EZ.addToCart render this section as part of the add request,
      // so the panel repaints from the same response that did the adding.
      EZ.cartSections = SECTION;
    }

    disconnectedCallback() {
      if (this.watcher) this.watcher.disconnect();

      // Only clear the hooks if they still point at this instance — the
      // theme editor removes and re-adds sections, and the new one will
      // have claimed them already.
      if (EZ.openCartDrawer === this.boundOpen) EZ.openCartDrawer = null;
      if (EZ.refreshCartDrawer === this.boundRefresh) EZ.refreshCartDrawer = null;
      if (EZ.paintCartDrawer === this.boundPaint) EZ.paintCartDrawer = null;
      if (EZ.cartSections === SECTION) EZ.cartSections = null;
    }

    /* -- Open / close ---------------------------------------------------- */

    open() {
      if (this.hasAttribute('data-open')) return;
      this.opener = document.activeElement;
      this.setAttribute('data-open', '');
      document.body.style.overflow = 'hidden';
    }

    close() {
      if (!this.hasAttribute('data-open')) return;
      this.removeAttribute('data-open');
      document.body.style.overflow = '';
    }

    onToggle() {
      var open = this.hasAttribute('data-open');

      document.querySelectorAll('[aria-controls="' + this.id + '"]').forEach(function (node) {
        node.setAttribute('aria-expanded', String(open));
      });

      if (open) {
        if (this.panel) this.panel.focus({ preventScroll: true });
        return;
      }

      // Closing: hand focus back to whatever opened the drawer, but only if
      // it is still on the page — a repaint can have replaced it.
      if (this.opener && document.contains(this.opener)) this.opener.focus();
      this.opener = null;
    }

    /**
     * aria-modal is a promise to the screen reader that nothing behind the
     * panel is reachable, so Tab has to actually stay inside it. The list is
     * rebuilt per keystroke because a repaint swaps the whole content out.
     */
    onKeydown(event) {
      if (event.key !== 'Tab' || !this.hasAttribute('data-open') || !this.panel) return;

      var focusable = Array.prototype.filter.call(
        this.panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
        ),
        function (node) { return node.offsetParent !== null; }
      );
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = document.activeElement;

      if (event.shiftKey && (active === first || active === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* -- Line edits ------------------------------------------------------ */

    onChange(event) {
      var input = event.target.closest('[data-line-qty]');
      if (!input) return;
      this.change(input.closest('[data-line]'), parseInt(input.value, 10) || 0);
    }

    onClick(event) {
      var remove = event.target.closest('[data-line-remove]');
      if (!remove) return;
      event.preventDefault();
      this.change(remove.closest('[data-line]'), 0);
    }

    change(line, quantity) {
      if (!line || this.busy) return;
      this.busy = true;
      line.setAttribute('data-busy', '');

      fetch(EZ.routes.cart_change + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: line.dataset.key,
          quantity: quantity,
          sections: SECTION,
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          return response.json().then(function (cart) {
            if (!response.ok) throw new Error(cart.description || cart.message || EZ.strings.error);
            return cart;
          });
        })
        .then(function (cart) {
          EZ.setCartCount(cart.item_count);
          if (!this.paint(cart.sections && cart.sections[SECTION])) {
            return this.refresh();
          }

          // The /cart page is showing the same lines; it has no way to know
          // they moved, so let the server re-render it.
          if (document.querySelector('[data-cart-root]')) window.location.reload();
        }.bind(this))
        .catch(function (error) {
          console.warn('[ezion] side cart:', error);
          line.removeAttribute('data-busy');
          EZ.toast(error.message || EZ.strings.error, null);
        })
        .then(function () {
          this.busy = false;
        }.bind(this));
    }

    /* -- Repaint --------------------------------------------------------- */

    /**
     * Swap in freshly rendered section markup. Only the inner wrapper is
     * replaced — the host and the panel stay, so an open drawer keeps its
     * transform and never flashes shut mid-update.
     */
    paint(html) {
      if (!html) return false;

      var incoming = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-sidecart-content]');
      var current = this.querySelector('[data-sidecart-content]');

      if (!incoming || !current) {
        console.warn('[ezion] side cart: no [data-sidecart-content] in the rendered section');
        return false;
      }

      current.replaceWith(incoming);
      return true;
    }

    /**
     * Render the section on its own. Only needed when a caller could not get
     * the markup out of the request it already made — the fallback path.
     */
    refresh() {
      return fetch(window.location.pathname + '?sections=' + SECTION, {
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('sections request failed: ' + response.status);
          return response.json();
        })
        .then(function (sections) {
          if (!this.paint(sections[SECTION])) throw new Error('sections response had no markup');
        }.bind(this));
    }
  }

  if (!customElements.get('ez-cart-drawer')) customElements.define('ez-cart-drawer', EzCartDrawer);

  /* --------------------------------------------------------------------
     Triggers
     -------------------------------------------------------------------- */

  /* The bag in the header, and anything else opting in. Both keep a real
     href to /cart, so this is an enhancement and never a dead link. */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-cart-drawer-toggle]');
    if (!trigger || !EZ.openCartDrawer) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();

    // Open first so the panel answers the tap immediately, then reconcile.
    // What is on screen is a server render of this page load, which the
    // /cart page's own line editing can have moved on from.
    EZ.openCartDrawer();
    if (EZ.refreshCartDrawer) {
      EZ.refreshCartDrawer().catch(function (error) {
        console.warn('[ezion] side cart could not refresh:', error);
      });
    }
  });

  /* Every add-to-cart on the site funnels through EZ.afterAdd. With the
     drawer present that means: pull the new cart, then slide it open. */
  EZ.afterAdd = function (response) {
    if (!EZ.openCartDrawer) {
      EZ.toast(EZ.strings.added || 'Added to bag', EZ.strings.viewBag);
      return;
    }

    // The add request rendered the section for us in the common case.
    var html = response && response.sections && response.sections[SECTION];
    if (EZ.paintCartDrawer(html)) {
      EZ.openCartDrawer();
      return;
    }

    // It didn't, so go and fetch it. Opening only after the panel has real
    // markup in it — an empty-looking bag is worse than a beat of delay.
    EZ.refreshCartDrawer()
      .then(function () { EZ.openCartDrawer(); })
      .catch(function (error) {
        console.warn('[ezion] side cart could not repaint:', error);
        EZ.toast(EZ.strings.added || 'Added to bag', EZ.strings.viewBag);
      });
  };
})();
